"use client";
import { Input } from "@/components/ui/input";
import { KeyboardEvent, useState } from "react";
import { SketchPicker } from "react-color";
import Button from "../ui/Button";
import { generateImages } from "@/lib/actions/generateImages";
import toast from "react-hot-toast";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "../ui/switch";
import { styleOptions, type StyleOption } from "@/lib/constants/styles";
import { useRouter, useSearchParams } from "next/navigation";
import { isValidHexaCode } from "@/lib/utils";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { signIn } from "next-auth/react";
import { Icon } from "@prisma/client";
import Icons from "./Icons";
import Link from "next/link";
import Image from "next/image";
import { MultiStepLoader } from "../ui/multi-step-loader";

function validQueryParamOrNull<Option>(
  query: string | null | undefined,
  options: Array<Option>,
) {
  if (query === null || query === undefined) {
    return null;
  }
  if (options.find((e) => e === query)) {
    return query as Option;
  }
  return null;
}

export default function GenerateIconForm({
  userId,
}: {
  userId: string | undefined;
}) {
  const router = useRouter();

  const queryParams = useSearchParams();
  const prefilledDescription = queryParams?.get("description");
  const queryParamColorHex = queryParams?.get("colorHex");
  const prefilledHexColor =
    queryParamColorHex && isValidHexaCode(queryParamColorHex)
      ? queryParamColorHex
      : null;
  const prefilledBackgroundColor = validQueryParamOrNull<"white" | "dark">(
    queryParams?.get("backgroundColor"),
    ["white", "dark"],
  );
  const prefilledStyleDescription = validQueryParamOrNull<string>(
    queryParams?.get("style"),
    styleOptions.map((style) => style.description),
  );
  const prefilledComplexity = validQueryParamOrNull<
    "Minimalistic" | "Balanced" | "Complex"
  >(queryParams?.get("complexity"), ["Minimalistic", "Balanced", "Complex"]);

  let prefilledStyle: StyleOption | undefined = undefined;
  if (prefilledStyleDescription) {
    prefilledStyle = styleOptions.find(
      (style) => style.description === prefilledStyleDescription,
    );
  }

  const prefilledAddColorElementToDescription = validQueryParamOrNull<
    "true" | "false"
  >(queryParams?.get("addColorElementToDescription"), ["true", "false"]);

  const formSchema = z.object({
    description: z.string().min(1).max(2000),
    colorHex: z.string(),
    addColorElementToDescription: z.boolean(),
    backgroundColor: z.union([z.literal("white"), z.literal("dark")]),
    style: z.object({
      apiDescription: z.string(),
      description: z.string(),
    }),
    complexity: z.union([
      z.literal("Minimalistic"),
      z.literal("Balanced"),
      z.literal("Complex"),
    ]),
  });

  const [numberOfImages, setNumberOfImages] = useState(1);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: prefilledDescription ?? "",
      colorHex: prefilledHexColor ?? "#000",
      addColorElementToDescription:
        prefilledAddColorElementToDescription === "true" ? true : false,
      backgroundColor: prefilledBackgroundColor ?? "white",
      style: prefilledStyle ?? {
        apiDescription: "",
        description: "",
      },
      complexity: prefilledComplexity ?? "Minimalistic",
    },
  });

  const { isSubmitting } = form.formState;

  const loadingStates = [
    {
      text: "Optimising prompts",
    },
    {
      text: "Applying styles",
    },
    {
      text: "Generating images",
    },
    {
      text: "Saving images to the cloud",
    },
    {
      text: "Finishing up",
    },
  ];

  const [generatedIcons, setGeneratedIcons] = useState<Icon[]>([]);

  const generateIcon = async (values: z.infer<typeof formSchema>) => {
    if (!userId) {
      await signIn("google", { callbackUrl: "/generate" });
      return;
    }
    try {
      const icons = await generateImages(
        userId,
        {
          ...values,
        },
        numberOfImages,
      );
      if ("error" in icons) {
        toast.error(icons.error, {
          duration: 7000,
        });
        return;
      }
      icons.some((icon) => {
        if ("error" in icon) {
          toast.error(icon.error, {
            duration: 7000,
          });
          return true;
        }
      });
      const filteredIcons = icons.filter(
        (icon) => !("error" in icon),
      ) as Icon[];
      setGeneratedIcons(filteredIcons);
      if (filteredIcons.length !== 0) {
        toast.success(
          "Success! Icons can be viewed below or on the dashboard.",
        );
        router.refresh();
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const checkKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") e.preventDefault();
  };

  return (
    <>
      <section className="container flex h-full flex-col gap-8 px-6 py-10 text-foreground md:px-28">
        <MultiStepLoader
          loadingStates={loadingStates}
          loading={isSubmitting}
          duration={3500}
          loop={false}
        />
        <Form {...form}>
          <form
            id="generate-icon-form"
            onSubmit={form.handleSubmit(generateIcon)}
            onKeyDown={(e) => checkKeyDown(e)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xl font-semibold">
                    Describe the icon
                  </FormLabel>
                  <FormDescription>
                    Tip: Use chatgpt to generate ideas for the description!
                  </FormDescription>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder="A dancing elephant"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="colorHex"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xl font-semibold">
                    Select the color theme
                  </FormLabel>
                  <FormControl>
                    <div className="flex flex-col gap-4 md:flex-row">
                      <Dialog>
                        <DialogTrigger className="max-w-fit">
                          <div
                            className="h-32 w-32 cursor-pointer rounded-xl hover:outline hover:outline-2 hover:outline-blue-500"
                            style={{
                              backgroundColor: field.value,
                            }}
                          />
                        </DialogTrigger>
                        <DialogContent
                          onOpenAutoFocus={(e) => e.preventDefault()}
                          className="max-w-xs"
                        >
                          <DialogHeader>
                            <DialogTitle>Select the color theme</DialogTitle>
                            <DialogDescription hidden>
                              Select the color theme for the icon
                            </DialogDescription>
                            <div className="flex w-full items-center justify-center pt-4">
                              <SketchPicker
                                styles={{
                                  default: {
                                    picker: {
                                      paddingTop: "1.5rem",
                                    },
                                  },
                                }}
                                disableAlpha
                                onChange={(color) => field.onChange(color.hex)}
                                color={field.value}
                              />
                            </div>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="addColorElementToDescription"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Chroma Imagine</FormLabel>
                    <FormDescription>
                      Automatically add elements associated with the colour to
                      the icon. Example: sapphires and stars for a dark blue
                      colour, meadows and sunset for a yellowish green colour.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="backgroundColor"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Dark background</FormLabel>
                    <FormDescription>
                      Choose a dark background for the icon. Otherwise, it would
                      default to a light background.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value === "dark"}
                      onCheckedChange={(checked) =>
                        field.onChange(checked ? "dark" : "white")
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="complexity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xl font-semibold">
                    Complexity
                  </FormLabel>
                  <FormDescription>
                    Choose the complexity of the icon
                  </FormDescription>
                  <FormControl>
                    <div className="w-full md:w-[30vw]">
                      <div className="mt-4 w-full pl-10 pr-6">
                        <Slider
                          min={1}
                          max={3}
                          step={1}
                          value={
                            field.value === "Minimalistic"
                              ? [1]
                              : field.value === "Balanced"
                                ? [2]
                                : [3]
                          }
                          onValueChange={(value) =>
                            field.onChange(
                              value[0] === 1
                                ? "Minimalistic"
                                : value[0] === 2
                                  ? "Balanced"
                                  : "Complex",
                            )
                          }
                        />
                      </div>
                      <div className="mt-4 flex justify-between">
                        <span>Minimalistic</span>
                        <span>Complex</span>
                      </div>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="style"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xl font-semibold">
                    Select the style
                  </FormLabel>
                  <FormControl>
                    <StyleSelector
                      selectedStyle={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div>
              <Label className="text-xl font-semibold">Number of logos</Label>
              <Input
                type="number"
                min={1}
                max={20}
                className="w-40"
                value={numberOfImages}
                onChange={(e) => setNumberOfImages(parseInt(e.target.value))}
              />
            </div>
            <div className="flex w-full items-center justify-center">
              <Button
                isLoading={isSubmitting}
                form="generate-icon-form"
                type="submit"
                className="mt-4 h-full rounded-2xl px-4 py-3 text-xl"
              >
                {userId ? "Generate Icon" : "Sign in to generate icon!"}
              </Button>
            </div>
          </form>
        </Form>
        {generatedIcons.length > 0 && (
          <>
            <Icons icons={generatedIcons} />
            <div className="flex w-full items-center justify-center">
              <Link href="/dashboard">
                <Button>View on dashboard</Button>
              </Link>
            </div>
          </>
        )}
      </section>
    </>
  );
}

function StyleSelector({
  selectedStyle,
  onChange,
}: {
  selectedStyle: StyleOption;
  onChange: (selectedStyle: StyleOption) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-4 gap-x-10 gap-y-6 md:grid-cols-4 md:gap-y-10 xl:grid-cols-6">
      {styleOptions.map((style) => (
        <StyleOption
          key={style.description}
          style={style}
          onClick={() => onChange(style)}
          selected={selectedStyle.description === style.description}
        />
      ))}
    </div>
  );
}

function StyleOption({
  style,
  onClick,
  selected,
}: {
  style: StyleOption;
  onClick?: () => void;
  selected: boolean;
}) {
  return (
    <div className="flex h-full w-full flex-col items-center gap-2">
      <div
        onClick={onClick}
        className={
          "aspect-square w-full cursor-pointer rounded-xl bg-white outline " +
          (selected
            ? "outline-4 outline-green-500"
            : "outline-2 outline-primary/20")
        }
      >
        {style.imgUrl && (
          <Image
            src={`${style.imgUrl}`}
            alt="AI Generated Icon"
            width={1024}
            height={1024}
            className="rounded-xl"
          />
        )}
      </div>
      <span className="text-center">{style.description}</span>
    </div>
  );
}
